import api from '../axiosConfig';
import { USERS, ROLES } from '../config/apiEndpoints';

const capitalizeStatus = (status) => {
    if (!status) return 'Active';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

export const UserService = {
    getById: async (id) => {
        try {
            const response = await api.get(USERS.BY_ID(id));
            const data = response.data;
            return data.data || data;
        } catch (error) {
            console.error('UserService getById error:', error);
            throw error;
        }
    },

    getRoles: async (params = {}) => {
        try {
            const axiosParams = {};
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
                if (params.limit) axiosParams.limit = params.limit;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(ROLES.BASE, { params: axiosParams });
            const data = response.data;
            return data;
        } catch (error) {
            console.error('UserService getRoles error:', error);
            throw error;
        }
    },

    getAll: async (params = {}) => {
        try {
            const axiosParams = {};
            if (params.search) axiosParams.search = params.search;
            
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
                if (params.limit) axiosParams.limit = params.limit;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            
            if (params.role && params.role !== 'All') axiosParams.role = params.role;
            if (params.hotel && params.hotel !== 'All') axiosParams.hotel = params.hotel;

            const response = await api.get(USERS.BASE, { params: axiosParams });
            return response.data;
        } catch (error) {
            console.error('UserService getAll error:', error);
            throw error;
        }
    },

    save: async (user) => {
        try {
            const isEditing = !!user.id;
            const url = isEditing ? USERS.BY_ID(user.id) : USERS.BASE;

            const payload = {
                name: user.name,
                email: user.email,
                roleId: user.role || user.roleId || user.role?.id,
                propertyIds: user.assignedProperties || (user.hotel ? [user.hotel] : []),
                customPermissions: user.permissions || user.customPermissions || [],
                status: capitalizeStatus(user.status || 'Active'),
                department: user.department || undefined,
                joiningDate: user.joiningDate || undefined,
                employeeId: user.employeeId || undefined,
                phone: user.phone || undefined,
                passwordMode: user.passwordMode || 'auto',
                password: user.passwordMode === 'manual' ? user.password : undefined,
                createLinkedStaff: user.createLinkedStaff,
            };

            const response = isEditing
                ? await api.patch(url, payload)
                : await api.post(url, payload);

            return response.data;
        } catch (error) {
            console.error('UserService save error:', error);
            throw error;
        }
    },

    delete: async (id) => {
        try {
            const response = await api.delete(USERS.BY_ID(id));
            return response.data;
        } catch (error) {
            console.error('UserService delete error:', error);
            throw error;
        }
    },

    // ─── Status Management ────────────────────────────────────────────────────

    updateStatus: async (id, status) => {
        try {
            const response = await api.patch(USERS.STATUS(id), { status: capitalizeStatus(status) });
            return response.data;
        } catch (error) {
            console.error('UserService updateStatus error:', error);
            throw error;
        }
    },

    // ─── Administrative Password Reset ────────────────────────────────────────

    resetPassword: async (id, { method = 'manual', temporaryPassword }) => {
        try {
            const response = await api.post(USERS.RESET_PASSWORD(id), { method, temporaryPassword });
            return response.data;
        } catch (error) {
            console.error('UserService resetPassword error:', error);
            throw error;
        }
    },

    // ─── User Security & MFA ──────────────────────────────────────────────────

    updateSecurity: async (id, securityData) => {
        try {
            const response = await api.patch(USERS.SECURITY(id), securityData);
            return response.data;
        } catch (error) {
            console.error('UserService updateSecurity error:', error);
            throw error;
        }
    },

    // ─── User Activity Logs ───────────────────────────────────────────────────

    getUserActivity: async (id, params = {}) => {
        try {
            const axiosParams = {};
            if (params.page !== undefined) axiosParams.page = params.page + 1;
            if (params.limit) axiosParams.limit = params.limit;
            const response = await api.get(USERS.ACTIVITY(id), { params: axiosParams });
            return response.data;
        } catch (error) {
            console.error('UserService getUserActivity error:', error);
            throw error;
        }
    },

    // ─── Export Users ─────────────────────────────────────────────────────────

    exportUsers: async (format = 'csv') => {
        try {
            const response = await api.get(USERS.EXPORT, { 
                params: { format },
                responseType: 'blob' 
            });
            return response;
        } catch (error) {
            console.error('UserService exportUsers error:', error);
            throw error;
        }
    },

    // ─── Bulk Import ──────────────────────────────────────────────────────────

    importValidate: async (file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.post(USERS.IMPORT_VALIDATE, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data;
        } catch (error) {
            console.error('UserService importValidate error:', error);
            throw error;
        }
    },

    importExecute: async (data) => {
        try {
            const response = await api.post(USERS.IMPORT_EXECUTE, data);
            return response.data;
        } catch (error) {
            console.error('UserService importExecute error:', error);
            throw error;
        }
    },

    // ─── Unlock User Account ──────────────────────────────────────────────────

    unlockUser: async (id) => {
        try {
            const response = await api.post(USERS.UNLOCK(id));
            return response.data;
        } catch (error) {
            console.error('UserService unlockUser error:', error);
            throw error;
        }
    },

    // ─── Update User Role ─────────────────────────────────────────────────────

    updateRole: async (id, roleId) => {
        try {
            const response = await api.put(USERS.USER_ROLE(id), { roleId });
            return response.data;
        } catch (error) {
            console.error('UserService updateRole error:', error);
            throw error;
        }
    },

    // ─── Update User Permissions ──────────────────────────────────────────────

    updatePermissions: async (id, permissions) => {
        try {
            const response = await api.put(USERS.USER_PERMISSIONS(id), { permissions });
            return response.data;
        } catch (error) {
            console.error('UserService updatePermissions error:', error);
            throw error;
        }
    },
};
